"use client";
import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { SectionHeading } from "@/components/common/decor";

const faqData = [
  {
    question: "How do I start the book publishing process?",
    answer:
      "Getting started is easy — just share your manuscript or idea with us. From there, our team walks you through every step: editing, formatting, cover design, and final publication, so you can stay focused on your story.",
  },
  {
    question: "Do I keep the rights to my book?",
    answer:
      "Yes. Unlike traditional publishers, you keep 100% ownership and royalties of your book. We are here to help you publish, not take your story away.",
  },
  {
    question: "Can you help me if I only have an idea?",
    answer:
      "Definitely. Our ghostwriters can turn your concept into a fully written manuscript, guiding you from brainstorming all the way to a finished draft.",
  },
  {
    question: "What is the difference between editing and proofreading?",
    answer:
      "Editing improves flow, structure, and clarity, while proofreading checks grammar, punctuation, and typos. Most authors benefit from both for a polished, publish-ready manuscript.",
  },
  {
    question: "Do you design custom book covers?",
    answer:
      "Yes — no templates. Our designers create unique, eye-catching covers and interior layouts tailored to your genre and target readership.",
  },
  {
    question: "Can you illustrate children's books?",
    answer:
      "Yes. We have illustrators who specialize in children's books, from playful characters to colorful, full-page story spreads.",
  },
  {
    question: "Where will my book be available?",
    answer:
      "We publish across major platforms including Amazon (Kindle and Print), Barnes & Noble, Kobo, Apple Books, and Google Play, and enable distribution to bookstores and libraries.",
  },
  {
    question: "How long does it take to publish my book after the manuscript is complete?",
    answer:
      "The publishing timeline typically ranges from 4–8 weeks after your final manuscript is approved. This includes formatting, cover design finalization, quality checks, and distribution setup. Rush options may be available for an additional fee.",
  },
  {
    question: "How much does it cost to publish a book?",
    answer:
      "The cost depends on the services you need — ghostwriting, editing, cover design, formatting, publishing, and marketing can be purchased individually or as a bundled package. We offer transparent Silver, Gold, and Platinum plans with no hidden fees, so you only pay for what your project requires.",
  },
  {
    question: "Do you provide an ISBN and copyright registration?",
    answer:
      "Yes. We can assign an ISBN for each format of your book and guide you through copyright registration so your work is fully protected. Because our agreement is non-exclusive, the ISBN and copyright remain associated with you as the author.",
  },
  {
    question: "What formats will my book be published in?",
    answer:
      "Your book can be published as an eBook (Kindle, EPUB), paperback, hardcover, and audiobook. We format each version to meet the technical specifications of every retailer.",
  },
  {
    question: "Do you offer book marketing after publishing?",
    answer:
      "Yes. Our marketing team helps with Amazon optimization, author websites, social media promotion, press releases, and paid advertising to help your book reach the right readers.",
  },
  {
    question: "Will I retain the rights to my book, and can I publish elsewhere too?",
    answer:
      "Yes. You retain full ownership and copyright of your work. Our publishing agreement is non-exclusive, so you are free to publish through other channels now or in the future.",
  },
  {
    question: "Do you offer international distribution, and how are royalties handled?",
    answer:
      "We provide global distribution to over 40 countries. Royalty rates vary by platform and region (typically 35–70% of net receipts), and we provide detailed monthly reports with sales by territory, platform, and format.",
  },
];

const FaqAccordians = () => {
  const [open, setOpen] = useState(0);

  return (
    <div className="bg-paper">
      {/* Header band */}
      <div className="relative overflow-hidden bg-ink bg-paper-grain px-4 py-20 text-center">
        <div aria-hidden className="pointer-events-none absolute -left-20 top-6 h-56 w-56 rounded-full bg-brand-500/25 blur-3xl" />
        <div aria-hidden className="pointer-events-none absolute -right-16 bottom-0 h-56 w-56 rounded-full bg-gold/15 blur-3xl" />
        <div className="relative">
          <span className="eyebrow mx-auto flex items-center justify-center gap-3 text-gold-soft">
            <span className="h-px w-8 bg-gold/70" />
            Frequently Asked Questions
            <span className="h-px w-8 bg-gold/70" />
          </span>
          <h1 className="mt-4 font-display text-[clamp(2rem,4vw,3.4rem)] font-semibold text-white">
            Still Have Questions? We&apos;ve Got Answers
          </h1>
        </div>
      </div>

      {/* Accordion */}
      <div className="mx-auto max-w-4xl px-4 py-16 md:py-20">
        <SectionHeading
          eyebrow="How Can We Help?"
          title="Answers to What Authors Ask Most"
          description="If you don't see your question here, reach out — we're always happy to help."
          className="mb-12"
        />

        <div className="space-y-4">
          {faqData.map((item, index) => {
            const isOpen = open === index;
            return (
              <div
                key={index}
                data-aos="fade-up"
                data-aos-delay={(index % 3) * 70}
                className={`overflow-hidden rounded-2xl border transition-all duration-300 ${
                  isOpen ? "border-gold/40 bg-white shadow-editorial" : "border-ink/10 bg-white hover:border-gold/30"
                }`}
              >
                <button
                  onClick={() => setOpen(isOpen ? -1 : index)}
                  className="flex w-full items-center justify-between gap-4 px-6 py-5 text-left"
                >
                  <div className="flex items-center gap-4">
                    <span className={`font-display text-lg font-semibold transition-colors ${isOpen ? "text-gold-deep" : "text-brand-600"}`}>
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3 className="font-display text-base font-semibold text-ink md:text-lg">{item.question}</h3>
                  </div>
                  <ChevronDown
                    size={22}
                    className={`shrink-0 text-gold transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                <div className={`grid transition-all duration-400 ease-in-out ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                  <div className="overflow-hidden">
                    <p className="px-6 pb-6 pl-16 text-sm leading-relaxed text-ink-soft md:text-base">
                      {item.answer}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default FaqAccordians;
